import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import UploadExtractionService from "../services/UploadExtractionService";

const BulkProcessingContext = createContext();

export const useBulkProcessing = () => {
  const context = useContext(BulkProcessingContext);
  if (!context) {
    throw new Error("useBulkProcessing must be used within BulkProcessingProvider");
  }
  return context;
};

export const BulkProcessingProvider = ({ children }) => {
  const [processingStatus, setProcessingStatus] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const uploadExtractionServiceRef = useRef(new UploadExtractionService());
  const onAllCompleteCallbackRef = useRef(null);

  // Auto-clear processing status when all files complete
  useEffect(() => {
    const allStatuses = Object.values(processingStatus);
    if (allStatuses.length > 0) {
      const allCompleted = allStatuses.every(
        (status) => status.status === "completed" || status.status === "failed"
      );

      if (allCompleted) {
        // Clear processing status after completion
        const timer = setTimeout(() => {
          setProcessingStatus({});
          // Call the registered callback to refresh file list
          if (onAllCompleteCallbackRef.current) {
            onAllCompleteCallbackRef.current();
          }
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [processingStatus]);

  const startProcessing = async (files, onProgressUpdate, onComplete) => {
    // Add new files to existing processing status
    setProcessingStatus((prev) => {
      const newStatus = { ...prev };
      files.forEach((file) => {
        newStatus[file.fileImportId] = {
          status: "processing",
          message: "Processing...",
          fileName: file.fileName,
        };
      });
      return newStatus;
    });

    try {
      // Progress update callback that updates both local state and parent callback
      const internalProgressUpdate = (fileId, status, message) => {
        setProcessingStatus((prev) => ({
          ...prev,
          [fileId]: {
            ...prev[fileId],
            status,
            message,
          },
        }));

        // Call the parent's progress update if provided
        if (onProgressUpdate) {
          onProgressUpdate(fileId, status, message);
        }
      };

      // Process all files in parallel
      const results = await uploadExtractionServiceRef.current.processBulkFiles(
        files,
        internalProgressUpdate
      );

      // Call completion callback if provided
      if (onComplete) {
        onComplete(results);
      }

      return results;
    } catch (error) {
      console.error("Error in bulk processing:", error);
      throw error;
    }
  };

  const clearProcessing = () => {
    setProcessingStatus({});
  };

  const isFileProcessing = (fileId) => {
    return !!processingStatus[fileId];
  };

  const getProcessingCount = () => {
    return Object.keys(processingStatus).length;
  };

  const hasActiveProcessing = () => {
    return Object.keys(processingStatus).length > 0;
  };

  const setOnAllCompleteCallback = (callback) => {
    onAllCompleteCallbackRef.current = callback;
  };

  const handleSelectedRows = (rows) => {
    setSelectedRows(rows);
  };

  const value = {
    processingStatus,
    startProcessing,
    clearProcessing,
    isFileProcessing,
    getProcessingCount,
    hasActiveProcessing,
    setOnAllCompleteCallback,
    selectedRows,
    handleSelectedRows,
  };

  return <BulkProcessingContext.Provider value={value}>{children}</BulkProcessingContext.Provider>;
};
